import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaTrash, FaPhone, FaMapMarkerAlt, FaUserGraduate, FaChevronDown, FaChevronUp, FaUsers, FaSearch, FaPlus } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import { Link } from 'react-router-dom';

interface Student {
  id: number;
  name: string;
  class_name: string;
}

interface Parent {
  id: number;
  name: string;
  phone_number: string;
  address: string;
  student_info: Student[];
}

const ShowParents: React.FC = () => {
  const [parents, setParents] = useState<Parent[]>([]);
  const [expandedParent, setExpandedParent] = useState<number | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get('/users/user');
        setUserRole(response.data.user.role);
        dispatch(setAuth(true));
      } catch (error) {
        console.error('Error fetching user data:', error);
        dispatch(setAuth(false));
      }
    };

    fetchUserData();
  }, [dispatch]);

  useEffect(() => {
    const fetchParents = async () => {
      setLoading(true);
      try {
        const response = await axios.get<any>('/Branches/parents/');
        setParents(response.data);
      } catch (error) {
        console.error('Error fetching parents:', error);
        setError('Failed to fetch parents');
      } finally {
        setLoading(false);
      }
    };

    fetchParents();
  }, []);

  const handleDelete = async (id: number, parentName: string) => {
    if (window.confirm(`Ma hubtaa inaad rabto inaad tirtirto ${parentName}?`)) {
      try {
        await axios.delete(`/Branches/parents/${id}/`);
        setParents(parents.filter(parent => parent.id !== id));
        setSuccessMessage('Waalidka si guul leh ayaa loo tirtiray');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error('Error deleting parent:', error);
        setError('Waalidka lama tirtiri karin');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  const toggleDetails = (parentId: number) => {
    setExpandedParent(expandedParent === parentId ? null : parentId);
  };

  // Filter parents by search term
  const filteredParents = parents.filter(parent =>
    parent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    parent.phone_number.includes(searchTerm) ||
    parent.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total students count
  const totalStudents = parents.reduce((sum, parent) => sum + parent.student_info.length, 0);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="text-center mt-5">
        <h4 className="text-muted">Ma aadan xaqiijin</h4>
        <a href="/login" className="btn btn-primary mt-3">
          Aad Login
        </a>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="container-fluid px-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
              <div>
                <h1 className="h2 fw-bold text-dark mb-2">👨‍👩‍👧‍👦 Maamulka Waalidiinta</h1>
                <p className="text-muted mb-0">Maamul macluumaadka waalidka/masuulka iyo ardayda ku xiran</p>
              </div>
              {userRole === 'admin' && (
                <Link to="/add-parent" className="btn btn-primary btn-lg rounded-pill px-4 shadow">
                  <FaPlus className="me-2" />
                  Ku Dar Waalid Cusub
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Alerts */}
        {successMessage && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-success alert-dismissible fade show border-0 shadow-sm" role="alert">
                <i className="bi bi-check-circle-fill me-2"></i>
                {successMessage}
                <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-danger alert-dismissible fade show border-0 shadow-sm" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)}></button>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="card border-0 shadow-sm bg-primary bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-primary fw-semibold mb-1 small">Waalidiinta Guud</p>
                    <h3 className="fw-bold mb-0 text-primary">{parents.length}</h3>
                  </div>
                  <div className="bg-primary bg-opacity-25 rounded-circle p-3">
                    <FaUsers className="text-primary" size={24} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm bg-success bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-success fw-semibold mb-1 small">Ardayda Ku Xiran</p>
                    <h3 className="fw-bold mb-0 text-success">{totalStudents}</h3>
                  </div>
                  <div className="bg-success bg-opacity-25 rounded-circle p-3">
                    <FaUserGraduate className="text-success" size={24} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm bg-info bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-info fw-semibold mb-1 small">Natiijooyinka</p>
                    <h3 className="fw-bold mb-0 text-info">{filteredParents.length}</h3>
                  </div>
                  <div className="bg-info bg-opacity-25 rounded-circle p-3">
                    <FaSearch className="text-info" size={24} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                {/* Search Bar */}
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <label className="form-label fw-semibold text-secondary small">Raadi Waalidiinta</label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-white border-2">
                        <FaSearch className="text-muted" />
                      </span>
                      <input
                        type="text"
                        className="form-control border-2"
                        placeholder="Ku raadi magaca, telefoonka, ama cinwaanka..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th style={{ width: '50px' }} className="text-muted fw-semibold small">#</th>
                        <th className="text-muted fw-semibold small">WAALIDKA/MASUULKA</th>
                        <th className="text-muted fw-semibold small">XIRIIRKA</th>
                        <th className="text-muted fw-semibold small">CINWAANKA</th>
                        <th style={{ width: '100px' }} className="text-center text-muted fw-semibold small">ARDAYDA</th>
                        {userRole === 'admin' && <th style={{ width: '120px' }} className="text-end text-muted fw-semibold small">FALALKA</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredParents.length === 0 ? (
                        <tr>
                          <td colSpan={userRole === 'admin' ? 6 : 5} className="text-center py-5">
                            <div className="text-muted">
                              <FaUsers size={48} className="mb-3 opacity-50" />
                              <p className="mb-0">Waalid lama helin</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredParents.map((parent, index) => (
                          <React.Fragment key={parent.id}>
                            <tr className={expandedParent === parent.id ? 'table-active' : ''}>
                              <td className="text-muted small fw-bold">{index + 1}</td>
                              <td className="fw-semibold text-dark">{parent.name}</td>
                              <td>
                                <a href={`tel:${parent.phone_number}`} className="text-decoration-none text-primary">
                                  <FaPhone size={12} className="me-2" />
                                  {parent.phone_number}
                                </a>
                              </td>
                              <td className="text-muted small">
                                <FaMapMarkerAlt size={12} className="me-2" />
                                {parent.address}
                              </td>
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                  onClick={() => toggleDetails(parent.id)}
                                >
                                  {parent.student_info.length}
                                  {expandedParent === parent.id ? (
                                    <FaChevronUp size={12} className="ms-2" />
                                  ) : (
                                    <FaChevronDown size={12} className="ms-2" />
                                  )}
                                </button>
                              </td>
                              {userRole === 'admin' && (
                                <td className="text-end">
                                  <button
                                    className="btn btn-danger btn-sm rounded-pill px-3"
                                    onClick={() => handleDelete(parent.id, parent.name)}
                                  >
                                    <FaTrash size={12} className="me-1" />
                                    Tirtir
                                  </button>
                                </td>
                              )}
                            </tr>
                            {expandedParent === parent.id && (
                              <tr>
                                <td colSpan={userRole === 'admin' ? 6 : 5} className="bg-light">
                                  <div className="p-3">
                                    <h6 className="fw-bold mb-3 text-dark">
                                      <FaUserGraduate className="me-2 text-primary" />
                                      Ardayda Ku Xiran ({parent.student_info.length})
                                    </h6>
                                    {parent.student_info.length > 0 ? (
                                      <div className="row g-3">
                                        {parent.student_info.map((student) => (
                                          <div key={student.id} className="col-md-6 col-lg-4">
                                            <div className="card border-0 shadow-sm">
                                              <div className="card-body">
                                                <div className="d-flex align-items-start gap-3">
                                                  <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                                                    <FaUserGraduate className="text-primary" size={20} />
                                                  </div>
                                                  <div className="flex-grow-1">
                                                    <h6 className="fw-bold mb-1 text-dark">{student.name}</h6>
                                                    <span className="badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-2">
                                                      {student.class_name}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-muted mb-0">Arday kuma xirna waalidkan.</p>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShowParents;
